import { FamilyPanel } from "@/components/radnici/FamilyPanel";
import { SickLeavePanel } from "@/components/radnici/SickLeavePanel";
import { WorkerRegistrationAddress } from "@/components/radnici/WorkerRegistrationAddress";
import { HealthTab, SafetyTab } from "@/components/radnici/HealthSafetyTabs";
import { SafetyProcessesPanel } from "@/components/radnici/SafetyProcessesPanel";
import { PermitCasesPanel } from "@/components/radnici/PermitCasesPanel";
import { ForeignWorkerFields } from "@/components/radnici/ForeignWorkerFields";
import { HzzRequestsPanel } from "@/components/hzz/HzzRequestsPanel";
import { WorkerOverviewPanel } from "@/components/radnici/WorkerOverviewPanel";
import { legacyWorkerTab, workerDestination, workerDocumentWorkspace, type WorkerTab } from "@/lib/workerTabs";
import { WorkerCardTransferFlow } from "@/components/radnici/WorkerCardTransferFlow";
import { WorkerSitesPanel } from "@/components/radnici/WorkerSitesPanel";
import { fetchWorkerOverview, type WorkerOverview } from "@/lib/workerOverview";
import { PermitDeregistrationHistory } from "@/components/radnici/PermitDeregistrationHistory";
import { PermitDeregistrationBanner } from "@/components/radnici/PermitDeregistrationBanner";
import { PermitRenewalDialog } from "@/components/radnici/PermitRenewalDialog";
import { errorMessage } from "@/lib/api";
import { EmployeeRecordSection } from "@/components/radnici/EmployeeRecordSection";
import { EmployeeRecordCreate } from "@/components/radnici/EmployeeRecordCreate";
import { elapsedCalendar, EmployeeBasicSections } from "@/components/radnici/EmployeeBasicSections";
import { PropertyList, PropertyItem, PropertySection, CopyValue } from "@/components/ui/property-list";
import { Select as DesignSelect } from "@/components/ui/design-system";
import { useNavigate } from "react-router-dom";
import { PageHeader, Tabs } from "@/components/ui/design-system";
import { countryLabel, recordLabel } from "@/lib/employeeRecordLabels";
import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { StructuredEvidence, StructuredEvidenceProvider } from "@/components/dokumenti/StructuredEvidence";
import { DocumentDataPanel } from "@/components/dokumenti/DocumentDataPanel";
import { DokumentiTab } from "@/components/dokumenti/DokumentiTab";
import { DokumentiIdentitetTab } from "@/components/radnici/DokumentiIdentitetTab";
import { EmptyState } from "@/components/ui/empty-state";
import { SkeletonTablica } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { datum, iznos } from "@/lib/format";
import {
  dohvatiDozvole,
  dohvatiKvalifikacije,
  dohvatiRadnika,
  dohvatiPayrollProfil,
  azurirajPayrollPostavke,
  inicijali,
  punoIme,
  RAZLOG_PRESTANKA,
  TIP_DOZVOLE,
  TIP_KVALIFIKACIJE,
  type Dozvola,
  type Kvalifikacija,
  type Radnik,
  type PayrollProfil,
  type PayrollPostavkeUpdate,
} from "@/lib/radnici";
import { procitajGreske } from "@/lib/greske";
import { oznakaDozvole, oznakaLijecnickog } from "@/lib/status";

/** Health details mount only while the dedicated health tab is open. */
type Tab = WorkerTab;
interface TabSpec { kljuc: Tab; naziv: string; ovlast?: [string, string] }
const TABOVI: TabSpec[] = [
  { kljuc: "pregled", naziv: "Pregled" },
  { kljuc: "gradilista", naziv: "Gradilišta", ovlast: ["raspored", "read"] },
  { kljuc: "podaci", naziv: "Podaci" },
  { kljuc: "dozvole", naziv: "Dozvole", ovlast: ["dozvola", "read"] },
  { kljuc: "obitelj", naziv: "Obitelj", ovlast: ["dozvola", "read"] },
  { kljuc: "identitet", naziv: "Identitet", ovlast: ["dozvola", "read"] },
  { kljuc: "zdravlje", naziv: "Zdravlje", ovlast: ["zdravlje", "read"] },
  { kljuc: "znr", naziv: "Zaštita na radu", ovlast: ["znr", "read"] },
  { kljuc: "dokumenti", naziv: "Dokumenti", ovlast: ["dokument", "read"] },
  { kljuc: "payroll", naziv: "Plaće", ovlast: ["payroll_profile", "read"] },
];

export function KarticaRadnikaPage() {
  const navigate=useNavigate();
  const { radnikId = "" } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { can } = useAuth();

  const [radnik, setRadnik] = useState<Radnik | null>(null);
  const [greska, setGreska] = useState<string | null>(null);
  const [overviewState, setOverviewState] = useState<{ workerId: string; data: WorkerOverview | null; error: string | null }>({ workerId: "", data: null, error: null });
  const overview = overviewState.workerId === radnikId ? overviewState.data : null;
  const requestedTab = searchParams.get("tab") ?? "pregled";
  const destination = workerDestination(requestedTab, searchParams.get("dio"));
  const allowedTab = (spec: TabSpec) => !(spec.kljuc === "payroll" && radnik?.izvor === "povijesni") && (!spec.ovlast || can(...spec.ovlast));
  const requested = TABOVI.find(spec => spec.kljuc === destination.tab);
  const tab: Tab = requested && allowedTab(requested) ? requested.kljuc : "pregled";
  const part = destination.part ?? (legacyWorkerTab(requestedTab) ? undefined : searchParams.get("dio"));
  const setTab = (target: string) => {
    const next = new URLSearchParams(searchParams); const targetDestination = workerDestination(target);
    next.set("tab", targetDestination.tab); next.delete("document");
    if (targetDestination.part) next.set("dio", targetDestination.part); else next.delete("dio");
    navigate({ pathname: location.pathname, search: next.toString(), hash: target === "kvalifikacije" ? "#kvalifikacije" : "" });
  };
  useEffect(() => {
    if (!legacyWorkerTab(requestedTab)) return;
    const next = new URLSearchParams(searchParams); next.set("tab", destination.tab);
    if (destination.part) next.set("dio", destination.part); else next.delete("dio");
    navigate({ pathname: location.pathname, search: next.toString(), hash: location.hash }, { replace: true });
  }, [requestedTab, destination.tab, destination.part, searchParams, navigate, location.pathname, location.hash]);
  const [medicalSummary, setMedicalSummary] = useState<{ workerId: string; text: string | null } | null>(null);
  const rememberMedical = useCallback((text: string | null) => setMedicalSummary({ workerId: radnikId, text }), [radnikId]);
  const healthOpen = tab === "zdravlje" && can("zdravlje", "read");
  const documentWorkspace = workerDocumentWorkspace(tab, part);
  const canOpenSection = (target: string) => {
    if (target === "zdravlje" && !can("zdravlje", "read")) return false;
    if (["znr", "oprema"].includes(target) && !can("znr", "read")) return false;
    return TABOVI.some(spec => spec.kljuc === workerDestination(target).tab && allowedTab(spec));
  };
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    void fetchWorkerOverview(radnikId, controller.signal)
      .then(data => { if (!controller.signal.aborted) setOverviewState({ workerId: radnikId, data, error: null }); })
      .catch((cause: unknown) => { if (!controller.signal.aborted) setOverviewState({ workerId: radnikId, data: null, error: errorMessage(cause, "Pregled se ne može učitati.") }); });
    return () => controller.abort();
  }, [radnikId, revision]);
  const [renewalOpen, setRenewalOpen] = useState(false);
  const [permitFocus, setPermitFocus] = useState(0);
  const [permits, setPermits] = useState<{ workerId: string; items: Dozvola[] | null; error: string | null }>({ workerId: "", items: null, error: null });
  const permitRead = can("dozvola", "read");
  const currentPermits = permits.workerId === radnikId ? permits.items : null;
  useEffect(() => {
    if (!permitRead) return;
    const controller = new AbortController();
    void dohvatiDozvole(radnikId, controller.signal).then((items) => { if (!controller.signal.aborted) setPermits({ workerId: radnikId, items, error: null }); }).catch((cause: unknown) => { if (!controller.signal.aborted) setPermits({ workerId: radnikId, items: null, error: errorMessage(cause, "Dozvole se ne mogu učitati. Osvježite karticu i pokušajte ponovno.") }); });
    return () => controller.abort();
  }, [radnikId, permitRead, revision]);
  useEffect(() => {
    if (tab !== "dozvole" || !permitFocus || !radnik) return;
    const heading = document.getElementById("permit-section-heading");
    heading?.scrollIntoView?.({ block: "start" }); heading?.focus();
  }, [tab, part, permitFocus, radnik]);
  useEffect(() => {
    const kontroler = new AbortController();
    dohvatiRadnika(radnikId, kontroler.signal)
      .then((value) => { if (!kontroler.signal.aborted) setRadnik(value); })
      .catch((error: unknown) => {
        if (kontroler.signal.aborted) return;
        setGreska("Radnik nije pronađen ili nemate ovlaštenje za njegov karton.");
        console.error(error);
      });
    return () => kontroler.abort();
  }, [radnikId, revision]);

  useEffect(() => {
    const ciljId = decodeURIComponent(location.hash.replace(/^#/, ""));
    if (!radnik || !ciljId) return;

    const pomakniNaCilj = (): boolean => {
      const cilj = document.getElementById(ciljId);
      if (!cilj) return false;
      cilj.scrollIntoView?.({ block: "start" });
      return true;
    };

    if (pomakniNaCilj()) return;
    const promatrac = new MutationObserver(() => {
      if (pomakniNaCilj()) promatrac.disconnect();
    });
    promatrac.observe(document.body, { childList: true, subtree: true });
    return () => promatrac.disconnect();
  }, [location.hash, radnik, tab]);

  const vidljiviTabovi = TABOVI.filter(allowedTab);

  if (greska) {
    return (
      <EmptyState
        naslov="Karton nije dostupan"
        objasnjenje={greska}
        akcija={
          <Link to="/zaposlenici">
            <Button variant="outline">Natrag na popis</Button>
          </Link>
        }
      />
    );
  }

  if (!radnik || radnik.id !== radnikId) {
    return <SkeletonTablica redaka={3} />;
  }

  const bivsiZaposlenik = radnik.aktivan === false;
  const dozvola = bivsiZaposlenik ? null : oznakaDozvole(radnik.dozvola_status, radnik.vrsta_ugovora);
  const istice =
    !bivsiZaposlenik &&
    radnik.dozvola_status &&
    ["istice", "isteklo", "nema"].includes(radnik.dozvola_status.stanje);

  return (
    <div className={documentWorkspace ? "avm-worker-card flex min-h-0 flex-1 flex-col gap-3 lg:overflow-hidden" : "avm-worker-card flex flex-col gap-6"}>
      <PageHeader title={punoIme(radnik)} leading={<span
          className="flex size-14 items-center justify-center rounded-full bg-[var(--color-status-idle-bg)] text-[var(--text-card)] font-semibold"
          role="img"
          aria-label={
            radnik.fotografija_placeholder !== false
              ? `Fotografija nije učitana; inicijali ${inicijali(radnik)}`
              : `Fotografija radnika ${punoIme(radnik)}`
          }
        >
          {inicijali(radnik)}
        </span>} breadcrumb={[{label:"Zaposlenici",to:"/zaposlenici"},{label:punoIme(radnik)}]} onBack={() => navigate("/zaposlenici")}
        meta={<><span>{radnik.radno_mjesto ?? "—"}</span>{radnik.drzavljanstvo_iso2 && <span>{countryLabel(radnik.drzavljanstvo_iso2)}</span>}{radnik.datum_pocetka_rada && <span>Staž: {elapsedCalendar(radnik.datum_pocetka_rada, radnik.datum_prestanka && bivsiZaposlenik ? new Date(`${radnik.datum_prestanka}T12:00:00`) : new Date())}</span>}{overview?.trenutni_raspored && <div className="worker-current-assignment">Trenutno: <strong>{overview.trenutni_raspored.gradiliste}</strong> od {datum(overview.trenutni_raspored.vrijedi_od)}</div>}{radnik.datum_prestanka && <span>Prestanak {datum(radnik.datum_prestanka)}{radnik.razlog_prestanka ? ` — ${RAZLOG_PRESTANKA[radnik.razlog_prestanka as keyof typeof RAZLOG_PRESTANKA] ?? radnik.razlog_prestanka}` : ""}</span>}</>}
        status={<><span className="worker-status" style={{ background: `var(--color-${radnik.izvor !== "povijesni" && radnik.aktivan ? "ok" : "neutral"}-solid)`, color: "white" }}>{radnik.izvor === "povijesni" ? "POVIJESNI RADNIK" : radnik.aktivan ? "AKTIVAN" : "BIVŠI DJELATNIK"}</span>{!bivsiZaposlenik && radnik.blokada_override_aktivan && <Badge ton="warn">Iznimka je odobrena</Badge>}</>}
        actions={<>{radnik.aktivan && radnik.izvor !== "povijesni" && can("raspored", "create") && can("raspored", "read") && <WorkerCardTransferFlow key={radnikId} workerId={radnikId} workerName={punoIme(radnik)} overview={overview} triggerLabel="Rasporedi na gradilište" onDone={() => setRevision(value => value + 1)} />}{can("radnik","update") && <Link to={`/zaposlenici/${radnikId}/uredi`}><Button variant="secondary">Uredi</Button></Link>}</>} />

      {radnik.datum_prestanka && <PermitDeregistrationBanner workerId={radnikId} revision={revision} />}
      {tab !== "pregled" && !!overview?.akcije.length && <button type="button" onClick={() => setTab("pregled")} className="min-h-11 text-left text-sm underline">{overview.akcije.length} stvari traži akciju → Pregled</button>}
      <div className="shrink-0 border-b border-[var(--color-border)]">
        <Tabs role="tablist" className="flex flex-wrap gap-1">
          {vidljiviTabovi.map((spec) => (
            <button
              key={spec.kljuc}
              type="button"
              role="tab"
              aria-selected={tab === spec.kljuc}
              onClick={() => setTab(spec.kljuc)}
              className={
                tab === spec.kljuc
                  ? "relative min-h-11 border-b-2 border-[var(--color-nav)] px-4 font-semibold"
                  : "relative min-h-11 border-b-2 border-transparent px-4 text-[var(--color-muted-foreground)]"
              }
            >
              {spec.naziv}
              {spec.kljuc === "podaci" && !!overview?.nedostajuca_polja.length && <span aria-hidden="true" className="ml-2 rounded-full bg-[var(--color-status-bad-bg)] px-2 text-[var(--color-status-bad-fg)]">{overview.nedostajuca_polja.length}</span>}
              {spec.kljuc === "zdravlje" && can("zdravlje", "read") ? (
                <span className="sr-only"> — pristup se evidentira</span>
              ) : null}
            </button>
          ))}
        </Tabs>
      </div>

      <StructuredEvidenceProvider refreshKey={revision} radnikId={radnikId} includeHealth={healthOpen} enabled={["dozvole", "identitet", "podaci", "payroll", "znr"].includes(tab) || healthOpen}><section role="tabpanel" className={documentWorkspace ? "flex min-h-0 flex-1 flex-col gap-3 lg:overflow-hidden" : "flex flex-col gap-4"}>
        {(tab === "podaci" || tab === "pregled") && <WorkerRegistrationAddress key={`${radnikId}-${revision}-${tab}`} worker={radnik} overview={tab === "pregled"} />}
        {tab === "pregled" ? overview ? <><FamilyPanel key={`family-summary-${radnikId}`} workerId={radnikId} summary onOpen={() => setTab("obitelj")} />{can("zdravlje", "read") && <button type="button" className="flex min-h-11 items-center rounded border p-4 text-left underline" onClick={() => setTab("zdravlje")}>Liječnički: {medicalSummary?.workerId === radnikId ? medicalSummary.text : "otvori Zdravlje za pregled postupka"}</button>}<SafetyProcessesPanel key={`training-overview-${radnikId}`} workerId={radnikId} kind="training" revision={revision} overview /><PermitCasesPanel key={`permit-overview-${radnikId}`} worker={radnik} permits={currentPermits} refreshKey={revision} overview /><WorkerOverviewPanel overview={overview} note={radnik.note} permitError={istice && permitRead && permits.workerId === radnikId ? permits.error : null} permitMissing={!!istice && permitRead && currentPermits?.length === 0} canOpen={canOpenSection} onAction={target => { setTab(target); if (target === "dozvole") setPermitFocus(value => value + 1); }} onRenew={istice && permitRead && can("dozvola", "update") && currentPermits?.length ? () => setRenewalOpen(true) : undefined} /></> : overviewState.workerId === radnikId && overviewState.error ? <div role="alert">{overviewState.error}<Button onClick={() => setRevision(value => value + 1)}>Pokušaj ponovno</Button></div> : <SkeletonTablica redaka={3} /> : null}
        {tab === "podaci" ? <><EmployeeBasicSections key={radnikId} worker={radnik} missing={overview?.nedostajuca_polja ?? []} onSaved={value => { setRadnik(value); setRevision(revision => revision + 1); }} />{can("radnik", "read") && <section id="kvalifikacije" className="space-y-3"><h2 className="text-lg font-semibold">Kvalifikacije i licence</h2><TabKvalifikacije key={radnikId} radnikId={radnikId} onSaved={() => setRevision(value => value + 1)} /></section>}</> : null}
        {tab === "gradilista" ? <WorkerSitesPanel key={radnikId} refreshKey={revision} workerId={radnikId} overview={overview} active={radnik.aktivan === true && radnik.izvor !== "povijesni"} onSaved={() => setRevision(value => value + 1)} /> : null}
        {tab === "dozvole" ? <section aria-labelledby="permit-section-heading"><h2 id="permit-section-heading" tabIndex={-1}>Dozvole radnika</h2><PermitCasesPanel key={radnikId} worker={radnik} permits={currentPermits} refreshKey={revision}>{eu => <TabDozvole eu={eu} radnikId={radnikId} onSaved={() => setRevision(value => value + 1)} stavke={currentPermits} greska={permits.workerId === radnikId ? permits.error : null} />}</PermitCasesPanel><HzzRequestsPanel workerId={radnikId} refreshKey={revision} />{radnik.datum_prestanka && <PermitDeregistrationHistory key={`odjava-${radnikId}-${revision}`} workerId={radnikId} />}</section> : null}
        {tab === "obitelj" && <FamilyPanel key={radnikId} workerId={radnikId} />}
        {tab === "identitet" ? <DokumentiIdentitetTab key={radnikId} radnikId={radnikId}>{permitRead && radnik.drzavljanstvo_iso2 && radnik.drzavljanstvo_iso2 !== "HR" && <ForeignWorkerFields key={radnikId} workerId={radnikId} />}</DokumentiIdentitetTab> : null}
        {healthOpen && <section aria-label="Zdravstveni podaci" className="space-y-3"><h2 className="text-lg font-semibold">Zdravlje</h2><p>Otvaranje zdravstvenih podataka evidentira se u zapisniku pristupa.</p><HealthTab key={radnikId} radnikId={radnikId} active={radnik.aktivan === true && radnik.izvor !== "povijesni"} onMedicalSummary={rememberMedical} onSaved={() => setRevision(value => value + 1)} /></section>}
        {tab === "znr" ? <section aria-label="Zaštita na radu" className="space-y-3"><h2 className="text-lg font-semibold">Zaštita na radu</h2><SafetyTab key={radnikId} radnikId={radnikId} active={radnik.aktivan === true && radnik.izvor !== "povijesni"} onSaved={() => setRevision(value => value + 1)} /></section> : null}
        {tab === "dokumenti" ? <DokumentiTab radnikId={radnikId} /> : null}
        {tab === "payroll" ? <TabPayroll radnikId={radnikId} /> : null}
      </section></StructuredEvidenceProvider>


      {renewalOpen && permitRead && can("dozvola", "update") && radnik.aktivan && !!currentPermits?.length && <PermitRenewalDialog key={radnikId} radnikId={radnikId} permits={currentPermits} expiry={radnik.dozvola_status?.datum_isteka} onClose={() => setRenewalOpen(false)} onSaved={() => { setRenewalOpen(false); setRevision((value) => value + 1); setTab("dozvole"); setPermitFocus((value) => value + 1); }} />}
      {dozvola ? <p className="sr-only">{dozvola.tekst}</p> : null}
    </div>
  );
}

function TabPayroll({ radnikId }: { radnikId: string }) {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const documentId = searchParams.get("document");
  const recordId = location.hash.startsWith("#record-") ? decodeURIComponent(location.hash.slice(8)) : null;
  if (documentId) return <section aria-label="Ugovor povezan s dokumentom" className="space-y-3">
    <div id={recordId ? `record-${recordId}` : undefined} className="flex flex-wrap items-center justify-between gap-3">
      <h2 className="font-semibold">Ugovor povezan s dokumentom</h2>
      <Link className="underline" to={`/zaposlenici/${radnikId}?tab=payroll`}>Otvori obračunski profil</Link>
    </div>
    <DocumentDataPanel key={documentId} radnikId={radnikId} documentId={documentId} showEdit={false}
      expectedRecordId={recordId ?? undefined} expectedRecordKind="employee_employment_terms" />
  </section>;
  return <><SickLeavePanel key={radnikId} workerId={radnikId} /><CurrentPayrollProfile radnikId={radnikId} /></>;
}

function CurrentPayrollProfile({ radnikId }: { radnikId: string }) {
  const { can } = useAuth();
  const [profil, setProfil] = useState<PayrollProfil | null>(null);
  const [greska, setGreska] = useState<string | null>(null);
  const [uredujem, setUredujem] = useState(false);

  useEffect(() => {
    const kontroler = new AbortController();
    dohvatiPayrollProfil(radnikId, kontroler.signal)
      .then(setProfil)
      .catch((error: unknown) => {
        if (kontroler.signal.aborted) return;
        setGreska(
          procitajGreske(error).opcenito[0] ?? "Obračunski profil nije dostupan.",
        );
      });
    return () => kontroler.abort();
  }, [radnikId]);

  if (greska) return <EmptyState naslov="Obračunski profil nije dostupan" objasnjenje={greska} />;
  if (!profil) return <SkeletonTablica redaka={3} />;
  const terms = profil.employment_terms;
  return <div id={terms?.id ? `record-${terms.id}` : undefined} className="flex flex-col gap-4">
    <StructuredEvidence kind="employee_employment_terms" recordId={terms?.id} />
    <div className="avm-surface flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-4">
      <div>
        <h2 className="font-semibold">Obračunski profil</h2>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Bruto plaća i tjedni fond dolaze iz Minimaxa. Kadrovska uređuje samo AVM podatke.
        </p>
      </div>
      {can("payroll_profile", "write") && terms ? (
        <Button onClick={() => setUredujem((value) => !value)}>
          {uredujem ? "Zatvori uređivanje" : "Uredi AVM podatke"}
        </Button>
      ) : null}
    </div>
    <div className="flex flex-col gap-4">
      <PayrollSekcija naslov="Minimax — zaključani podaci" redovi={[
        ["Vrsta zaposlenja", recordLabel("employee_employment_terms", "employment_type", terms?.employment_type)],
        ["Vrsta ugovora", recordLabel("employee_employment_terms", "contract_type", terms?.contract_type)],
        ["Početak ugovora", terms?.contract_start_on ? datum(terms.contract_start_on) : null],
        ["Kraj ugovora", terms?.contract_end_on ? datum(terms.contract_end_on) : null],
        ["Ugovorena bruto plaća", terms?.contracted_gross_salary ? `${iznos(terms.contracted_gross_salary)} €` : null],
        ["Tjedni fond", terms?.weekly_hours ? `${terms.weekly_hours} h` : null],
        ["Zadnja Minimax sinkronizacija", terms?.gross_salary_synchronized_at ? datum(terms.gross_salary_synchronized_at) : null],
      ]} />
    {uredujem && terms ? (
      <PayrollPostavkeForma
        radnikId={radnikId}
        profil={profil}
        onSpremljeno={(novi) => {
          setProfil(novi);
          setUredujem(false);
        }}
      />
    ) : null}
      <PayrollSekcija onEdit={can("payroll_profile", "write") && terms ? () => setUredujem(true) : undefined} naslov="AVM — dogovoreni podaci" redovi={[
        ["Dogovorena neto satnica", terms?.net_hourly_rate ? `${iznos(terms.net_hourly_rate)} € / h` : null],
        ["Postotak zaposlenja", terms?.employment_percentage ? `${terms.employment_percentage} %` : null],
        ["Prehrana", terms?.meal_benefit_mode === "employer_provided" ? "osigurava poslodavac" : terms?.meal_benefit_mode === "cash_payment" ? "novčana isplata" : terms?.meal_benefit_mode === "none" ? "bez naknade" : null],
        ["Mjesečni iznos prehrane", terms?.meal_allowance ? `${iznos(terms.meal_allowance)} €` : null],
        ["Staž kod poslodavca od", terms?.seniority_start_date ? datum(terms.seniority_start_date) : null],
      ]} />
      <PayrollSekcija naslov="Računi" redovi={profil.accounts.map((a) => [a.account_type, a.iban])} />
      <PayrollSekcija naslov="Obustave" redovi={profil.deductions.map((d) => [d.name, `${iznos(d.value)} € (${d.calculation_method})`])} />
      <PayrollSekcija naslov="Uzdržavani" redovi={profil.dependents.map((d) => [d.relationship, d.full_name])} />
    </div>
  </div>;
}

function PayrollPostavkeForma({
  radnikId,
  profil,
  onSpremljeno,
}: {
  radnikId: string;
  profil: PayrollProfil;
  onSpremljeno: (profil: PayrollProfil) => void;
}) {
  const terms = profil.employment_terms!;
  const [vrijednosti, setVrijednosti] = useState<PayrollPostavkeUpdate>({
    net_hourly_rate: terms.net_hourly_rate ?? "",
    employment_percentage: terms.employment_percentage ?? "100",
    meal_benefit_mode: terms.meal_benefit_mode ?? "employer_provided",
    meal_allowance: terms.meal_allowance,
    seniority_start_date: terms.seniority_start_date ?? "",
    full_month_insurance: terms.full_month_insurance ?? true,
    pension_pillar_one: terms.pension_pillar_one ?? true,
    pension_pillar_two: terms.pension_pillar_two ?? true,
    health_contribution_exempt: terms.health_contribution_exempt ?? false,
  });
  const [sprema, setSprema] = useState(false);
  const [greska, setGreska] = useState<string | null>(null);
  const control = "h-[var(--spacing-control)] rounded-[var(--radius-control)] border border-[var(--color-input-border)] bg-white px-3";

  async function spremi(): Promise<void> {
    setSprema(true);
    setGreska(null);
    try {
      const novi = await azurirajPayrollPostavke(radnikId, {
        ...vrijednosti,
        meal_allowance: vrijednosti.meal_benefit_mode === "cash_payment"
          ? vrijednosti.meal_allowance
          : null,
      });
      onSpremljeno(novi);
    } catch (error) {
      setGreska(procitajGreske(error).opcenito[0] ?? "Spremanje nije uspjelo.");
      setSprema(false);
    }
  }

  return <section className="avm-surface rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-4">
    <h2 className="mb-4 font-semibold">AVM podaci za obračun</h2>
    {greska ? <p role="alert" className="mb-3 text-[var(--color-status-bad-fg)]">{greska}</p> : null}
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <label className="flex flex-col gap-2"><span className="oznaka">Dogovorena neto satnica (EUR)</span><input required type="number" min="0.000001" step="0.000001" value={vrijednosti.net_hourly_rate} onChange={(e) => setVrijednosti({ ...vrijednosti, net_hourly_rate: e.target.value })} className={`${control} broj`} /></label>
      <label className="flex flex-col gap-2"><span className="oznaka">Postotak zaposlenja</span><input required type="number" min="0.01" max="100" step="0.01" value={vrijednosti.employment_percentage} onChange={(e) => setVrijednosti({ ...vrijednosti, employment_percentage: e.target.value })} className={`${control} broj`} /></label>
      <label className="flex flex-col gap-2"><span className="oznaka">Staž kod ovog poslodavca od</span><input required type="date" value={vrijednosti.seniority_start_date} onChange={(e) => setVrijednosti({ ...vrijednosti, seniority_start_date: e.target.value })} className={control} /></label>
      <label className="flex flex-col gap-2"><span className="oznaka">Način prehrane</span><DesignSelect value={vrijednosti.meal_benefit_mode} onChange={(e) => setVrijednosti({ ...vrijednosti, meal_benefit_mode: e.target.value as PayrollPostavkeUpdate["meal_benefit_mode"] })} className={control}><option value="employer_provided">Osigurava poslodavac</option><option value="cash_payment">Novčana isplata</option><option value="none">Bez naknade</option></DesignSelect></label>
      {vrijednosti.meal_benefit_mode === "cash_payment" ? <label className="flex flex-col gap-2"><span className="oznaka">Mjesečni iznos prehrane (EUR)</span><input required type="number" min="0.01" step="0.01" value={vrijednosti.meal_allowance ?? ""} onChange={(e) => setVrijednosti({ ...vrijednosti, meal_allowance: e.target.value })} className={`${control} broj`} /></label> : null}
    </div>
    <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {([
        ["full_month_insurance", "Osiguran cijeli mjesec"],
        ["pension_pillar_one", "Mirovinsko I. stup"],
        ["pension_pillar_two", "Mirovinsko II. stup"],
        ["health_contribution_exempt", "Oslobođen doprinosa za zdravstvo"],
      ] as const).map(([field, label]) => <label key={field} className="flex items-center gap-2"><input type="checkbox" checked={vrijednosti[field]} onChange={(e) => setVrijednosti({ ...vrijednosti, [field]: e.target.checked })} className="size-5 min-h-0" /><span>{label}</span></label>)}
    </div>
    <Button className="mt-5" disabled={sprema || !vrijednosti.net_hourly_rate || !vrijednosti.seniority_start_date} onClick={() => void spremi()}>{sprema ? "Spremanje…" : "Spremi AVM podatke"}</Button>
  </section>;
}

function PayrollSekcija({ naslov, redovi, onEdit }: { naslov: string; redovi: (string | null | undefined)[][]; onEdit?: () => void }) {
  return <PropertySection title={naslov} onEdit={onEdit}>
    {redovi.length ? <PropertyList>{redovi.map(([lijevo, desno], i) => <PropertyItem key={`${lijevo}-${i}`} label={lijevo} onAdd={onEdit}>{desno && /^[A-Z]{2}\d{2}[A-Z0-9 ]{10,}$/.test(desno) ? <CopyValue value={desno} label="IBAN" group /> : desno}</PropertyItem>)}</PropertyList> : <p className="text-[var(--color-muted-foreground)]">Nema evidentiranih podataka.</p>}
  </PropertySection>;
}


function usePodresurs<T>(
  dohvat: (id: string, signal?: AbortSignal) => Promise<T[]>,
  radnikId: string,
): { stavke: T[] | null; greska: string | null; refresh: () => void } {
  const [revision, setRevision] = useState(0);
  const [stavke, setStavke] = useState<T[] | null>(null);
  const [greska, setGreska] = useState<string | null>(null);

  useEffect(() => {
    const kontroler = new AbortController();
    dohvat(radnikId, kontroler.signal)
      .then(items => { if (!kontroler.signal.aborted) { setStavke(items); setGreska(null); } })
      .catch((error: unknown) => {
        if (kontroler.signal.aborted) return;
        setGreska("Podaci se ne mogu učitati.");
        console.error(error);
      });
    return () => kontroler.abort();
  }, [dohvat, radnikId, revision]);

  return { stavke, greska, refresh: () => setRevision(value => value + 1) };
}

function TabDozvole({ radnikId, stavke: allRecords, greska, onSaved, eu = false }: { eu?: boolean; radnikId: string; stavke: Dozvola[] | null; greska: string | null; onSaved: () => void }) {

  const stavke = eu ? allRecords?.filter(record => record.tip === "potvrda_prijave_egp") ?? null : allRecords;
  return <div className="space-y-4">
    <EmployeeRecordCreate permitType={eu ? "potvrda_prijave_egp" : undefined} key={radnikId} kind="permit" radnikId={radnikId} onSaved={onSaved} />
    {greska ? <EmptyState naslov="Dozvole nisu dostupne" objasnjenje={greska} /> : !stavke ? <SkeletonTablica redaka={2} /> : stavke.length === 0 ? (
      <EmptyState
        naslov="Nema zabilježenih dozvola"
        objasnjenje="Upiši dozvolu gumbom gore ili je dodaj skenom kroz Ladicu."
      />
    ) : <div className="space-y-4">{stavke.map(dozvola => <div key={dozvola.id} id={`record-${dozvola.id}`}>
    {dozvola.revoked_on && <p className="rounded bg-orange-50 p-3 font-semibold text-orange-950">Dozvola ukinuta {datum(dozvola.revoked_on)}</p>}
    <EmployeeRecordSection title={TIP_DOZVOLE[dozvola.tip] ?? dozvola.tip} record={dozvola} permission="dozvola" endpoint={`/radnici/${radnikId}/dozvole/${dozvola.id}`} onSaved={onSaved} fields={[
      { key: "tip", label: "Tip", required: true, options: TIP_DOZVOLE },
      { key: "broj_dozvole", label: "Broj" },
      { key: "izdavatelj", label: "Izdavatelj" },
      { key: "zemlja_iso2", label: "Zemlja", format: countryLabel },
      { key: "datum_izdavanja", label: "Izdana", type: "date", required: true },
      { key: "datum_isteka", label: "Ističe", type: "date", required: true, expires: true },
      { key: "datum_podnosenja_produljenja", label: "Zahtjev za produljenje", type: "date", format: value => `Podnesen ${datum(value)}` },
      { key: "napomena", label: "Napomena" },
    ]}><StructuredEvidence kind="dozvola" recordId={dozvola.id} /></EmployeeRecordSection>
  </div>)}</div>}
  </div>;
}

function TabKvalifikacije({ radnikId, onSaved }: { radnikId: string; onSaved: () => void }) {
  const { stavke, greska, refresh } = usePodresurs<Kvalifikacija>(
    dohvatiKvalifikacije,
    radnikId,
  );

  return <div className="space-y-3">
    <EmployeeRecordCreate kind="qualification" radnikId={radnikId} onSaved={() => { refresh(); onSaved(); }} />
    {greska ? <EmptyState naslov="Kvalifikacije nisu dostupne" objasnjenje={greska} /> : !stavke ? <SkeletonTablica redaka={2} /> : stavke.length === 0 ? (
      <EmptyState
        naslov="Nema zabilježenih kvalifikacija"
        objasnjenje="Stručno obrazovanje, licence i certifikati upisuju se u karton radnika."
      />
    ) : (
    <ul className="flex flex-col gap-2">
      {stavke.map((zapis) => {
        const oznaka = zapis.trajno ? { ton: "ok" as const, tekst: "Trajno" } : zapis.datum_isteka
          ? oznakaLijecnickog({
              smije_raditi: true,
              vrijedi_do: zapis.datum_isteka,
            })
          : null;
        return (
          <li
            key={zapis.id} id={`record-${zapis.id}`}
            className="flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-4"
          >
            <div className="w-full"><div className="mb-2 flex items-center gap-2"><span>{zapis.naziv}</span>{oznaka ? <Badge ton={oznaka.ton}>{oznaka.tekst}</Badge> : null}</div>
              <EmployeeRecordSection allowPermanentQualification onSaved={() => { refresh(); onSaved(); }} title="Podaci kvalifikacije" record={zapis} permission="radnik" endpoint={`/radnici/${radnikId}/kvalifikacije/${zapis.id}`} fields={[
                { key: "naziv", label: "Naziv", required: true },
                { key: "tip", label: "Tip", required: true, options: TIP_KVALIFIKACIJE },
                { key: "izdavatelj", label: "Izdavatelj" },
                { key: "datum_izdavanja", label: "Izdano", type: "date" },
                { key: "datum_isteka", label: "Vrijedi do", type: "date", expires: true },
              ]}><StructuredEvidence kind="kvalifikacija" recordId={zapis.id} /></EmployeeRecordSection>
            </div>
          </li>
        );
      })}
    </ul>
  )}</div>;
}
